import React, { useState, useEffect } from 'react';
import {
    Bell, AlertTriangle, Pin, Calendar, Clock,
    ExternalLink, Paperclip, Eye, Download, X,
    ChevronRight, CheckCircle, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import FileViewer from '../components/Tasks/FileViewer';

const EmployeeNoticesPage = () => {
    const navigate = useNavigate();
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [filter, setFilter] = useState('all'); // all, unread, read, reminders

    useEffect(() => {
        fetchNotices();
    }, []);

    const fetchNotices = async () => {
        setLoading(true);
        try {
            const response = await api.get('/notices/employee/my-notices');
            setNotices(response.data.notices || []);
        } catch (error) {
            console.error('Error fetching notices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNoticeClick = async (notice) => {
        setSelectedNotice(notice);

        // Mark as viewed if not already
        if (!notice.viewed) {
            try {
                await api.post(`/notices/employee/${notice.id}/view`);
                // Update local state
                setNotices(prev => prev.map(n =>
                    n.id === notice.id ? { ...n, viewed: true, viewed_at: new Date().toISOString() } : n
                ));
            } catch (error) {
                console.error('Error marking notice as viewed:', error);
            }
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent':
                return <AlertTriangle className="w-5 h-5 text-red-500" />;
            case 'pinned':
                return <Pin className="w-5 h-5 text-amber-500" />;
            default:
                return <Bell className="w-5 h-5 text-primary" />;
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'urgent':
                return 'border-l-4 border-l-red-500 bg-red-50';
            case 'pinned':
                return 'border-l-4 border-l-amber-500 bg-amber-50';
            default:
                return 'border-l-4 border-l-primary';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffTime = now - date;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
            if (diffHours === 0) {
                const diffMinutes = Math.floor(diffTime / (1000 * 60));
                return `${diffMinutes} minutes ago`;
            }
            return `${diffHours} hours ago`;
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return `${diffDays} days ago`;
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        }
    };

    const filteredNotices = notices.filter(notice => {
        if (filter === 'unread') return !notice.viewed;
        if (filter === 'read') return notice.viewed;
        if (filter === 'reminders') return notice.is_reminder;
        return true;
    });

    const unreadCount = notices.filter(n => !n.viewed).length;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-4 lg:px-6 py-4 sm:py-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Notices</h1>
                    <p className="text-sm text-gray-500 mt-1">Stay updated with company announcements</p>
                </div>
            </div>

            <div className="px-4 lg:px-6 py-4 sm:py-6">

                {/* Stats & Filter */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Bell className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-800">{notices.length}</p>
                                    <p className="text-xs text-gray-500">Total Notices</p>
                                </div>
                            </div>
                            {unreadCount > 0 && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm font-medium text-amber-700">
                                        {unreadCount} unread
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2">
                            {['all', 'unread', 'read', 'reminders'].map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {f.charAt(0).toUpperCase() + f.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Notices List */}
                {loading ? (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading notices...</p>
                    </div>
                ) : filteredNotices.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {filter === 'unread' ? 'No unread notices' : filter === 'read' ? 'No read notices' : 'No notices yet'}
                        </h3>
                        <p className="text-gray-600">
                            {filter === 'all' ? 'Company notices will appear here' : 'Try changing the filter'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredNotices.map((notice) => (
                            <div
                                key={notice.id}
                                onClick={() => handleNoticeClick(notice)}
                                className={`bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all ${getPriorityStyle(notice.priority)} ${!notice.viewed ? 'ring-2 ring-primary/20' : ''}`}
                            >
                                <div className="p-5">
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${notice.priority === 'urgent' ? 'bg-red-100' :
                                            notice.priority === 'pinned' ? 'bg-amber-100' : 'bg-primary/10'
                                            }`}>
                                            {getPriorityIcon(notice.priority)}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-semibold text-gray-800 text-lg">
                                                            {notice.title}
                                                        </h3>
                                                        {!notice.viewed && (
                                                            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full font-medium">
                                                                New
                                                            </span>
                                                        )}
                                                        {notice.priority === 'urgent' && (
                                                            <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full font-medium">
                                                                Urgent
                                                            </span>
                                                        )}
                                                        {notice.is_reminder && (
                                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium flex items-center gap-1">
                                                                <Bell className="w-3 h-3" /> Reminder
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-gray-600 text-sm line-clamp-2">
                                                        {notice.content?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                    </p>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            </div>

                                            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(notice.published_date)}
                                                </span>
                                                {notice.attachments && notice.attachments.length > 0 && (
                                                    <span className="flex items-center gap-1 text-primary">
                                                        <Paperclip className="w-3 h-3" />
                                                        {notice.attachments.length} file(s)
                                                    </span>
                                                )}
                                                {notice.external_link && (
                                                    <span className="flex items-center gap-1 text-primary">
                                                        <ExternalLink className="w-3 h-3" />
                                                        Link
                                                    </span>
                                                )}
                                                {notice.viewed && (
                                                    <span className="flex items-center gap-1 text-green-600">
                                                        <CheckCircle className="w-3 h-3" />
                                                        Viewed
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Notice Detail Modal */}
                {selectedNotice && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className={`px-6 py-4 border-b border-gray-200 ${selectedNotice.priority === 'urgent' ? 'bg-red-600' :
                                selectedNotice.priority === 'pinned' ? 'bg-amber-500' :
                                    'bg-primary'
                                }`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            {getPriorityIcon(selectedNotice.priority)}
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-white">
                                                {selectedNotice.title}
                                            </h2>
                                            <p className="text-white/80 text-sm mt-1">
                                                Posted {formatDate(selectedNotice.published_date)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSelectedNotice(null)}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5 text-white" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {/* Notice Content */}
                                <div
                                    className="prose prose-sm max-w-none text-gray-700"
                                    dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
                                />

                                {/* External Link */}
                                {selectedNotice.external_link && (
                                    <div className="mt-6">
                                        <a
                                            href={selectedNotice.external_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            <span className="font-medium">Open External Link</span>
                                        </a>
                                    </div>
                                )}

                                {/* Attachments */}
                                {selectedNotice.attachments && selectedNotice.attachments.length > 0 && (
                                    <div className="mt-6">
                                        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                            <Paperclip className="w-4 h-4" />
                                            Attachments ({selectedNotice.attachments.length})
                                        </h4>
                                        <div className="space-y-2">
                                            {selectedNotice.attachments.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-white rounded-lg border border-gray-200">
                                                            <Paperclip className="w-4 h-4 text-gray-500" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-800 text-sm">
                                                                {file.file_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {file.file_type}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setPreviewFile(file)}
                                                            className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                                            title="Preview"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <a
                                                            href={file.file_url}
                                                            download={file.file_name}
                                                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                            title="Download"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Expiry Info */}
                                {selectedNotice.expiry_date && (
                                    <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                                        <Clock className="w-4 h-4" />
                                        <span>
                                            This notice expires on {new Date(selectedNotice.expiry_date).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
                                {selectedNotice.is_reminder && selectedNotice.source_activity_type && (
                                    <button
                                        onClick={() => {
                                            setSelectedNotice(null);
                                            const routes = {
                                                task: '/employee/my-activities',
                                                survey: '/employee/my-surveys',
                                                compliance: '/employee/my-activities',
                                                course_item: '/employee/my-courses'
                                            };
                                            navigate(routes[selectedNotice.source_activity_type] || '/employee/my-activities');
                                        }}
                                        className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ArrowRight className="w-4 h-4" />
                                        Go to {selectedNotice.source_activity_type === 'task' ? 'Tasks' :
                                            selectedNotice.source_activity_type === 'survey' ? 'Surveys' :
                                                selectedNotice.source_activity_type === 'compliance' ? 'Tasks' :
                                                    selectedNotice.source_activity_type === 'course_item' ? 'Courses' : 'Activity'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setSelectedNotice(null)}
                                    className={`${selectedNotice.is_reminder && selectedNotice.source_activity_type ? 'flex-1' : 'w-full'} px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors`}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* File Viewer */}
                <FileViewer
                    file={previewFile}
                    isOpen={!!previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            </div>
        </div>
    );
};

export default EmployeeNoticesPage;

